import { useEffect, useMemo } from 'react';

import { getSessionId } from 'features/analytics/providers/amplitude/package';

import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { RunningExperiments, useExperiments } from 'state/experiments';

import {
  getAmplitudeSessionExposureDedupeKey,
  getExperimentAssignmentExposureKey,
} from './experiment-assignment-exposure-key';
import { minimalCartExposureSessionLatch } from './minimal-cart-exposure-sessions';

export const getMinimalCartExperimentSessionDedupeKey = getAmplitudeSessionExposureDedupeKey;

export const clearMinimalCartExperimentExposureSessionsForTests = () => {
  minimalCartExposureSessionLatch.clear();
};

const useMinimalCartExperimentAssignment = () => {
  const { getVariantValue } = useExperiments();

  return getVariantValue(RunningExperiments.EnableMinimalCart);
};

export const useMinimalCartExperimentVariant = () => useMinimalCartExperimentAssignment()?.value;

export const useEnableMinimalCartExperiment = () => {
  const variantValue = useMinimalCartExperimentVariant();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  return variantValue === 'treatment' && isUnifiedMenuEnabled;
};

export const useExposeMinimalCartExperiment = (shouldExpose = true) => {
  const { logExposure } = useExperiments();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const variant = useMinimalCartExperimentAssignment();
  const variantValue = variant?.value;
  const amplitudeSessionId = getSessionId()?.toString();
  const dedupeKey = getMinimalCartExperimentSessionDedupeKey(amplitudeSessionId);
  const assignmentExposureKey = useMemo(
    () =>
      dedupeKey && variant ? getExperimentAssignmentExposureKey({ dedupeKey, variant }) : undefined,
    [dedupeKey, variant]
  );

  useEffect(() => {
    if (
      !shouldExpose ||
      !isUnifiedMenuEnabled ||
      !variantValue ||
      !assignmentExposureKey ||
      minimalCartExposureSessionLatch.hasHandled(assignmentExposureKey)
    ) {
      return;
    }

    if (logExposure(RunningExperiments.EnableMinimalCart, { dedupeKey })) {
      minimalCartExposureSessionLatch.markHandled(assignmentExposureKey);
    }
  }, [
    assignmentExposureKey,
    dedupeKey,
    isUnifiedMenuEnabled,
    logExposure,
    shouldExpose,
    variantValue,
  ]);
};
